import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import ScreenshotExpiredPage from "../../mobile-sliding/components/ScreenshotExpiredPage";
import LandscapeGoogleAdCreative from "../components/LandscapeGoogleAdLayouts";
import {
    ClueLayout,
    CtaLayout,
    DailyLayout,
    DictionaryLayout,
    GameplayLayout,
    HeroLayout,
    JourneyLayout,
    MasterJourneyLayout,
    ThemesLayout,
} from "../components/ScreenshotMarketingLayouts";
import { screenshotSceneKey, useScreenshotTranslation } from "../../../lib/screenshotI18n";
import { screenshotFontStack, screenshotShellFontSx } from "../../../lib/screenshotFonts";
import { markScreenshotNotReady, signalScreenshotSceneReady } from "../../../lib/screenshotReady";
import { loadScreenshotWordWheel } from "../lib/screenshotApi";
import { resolveStandaloneBackgroundUrl } from "../lib/screenshotStoryBackground";
import { isScreenshotIphoneBackgroundKey, screenshotIphoneBgSrc } from "../lib/screenshotIphoneBackgrounds";
import { isScreenshotSceneExpired } from "../lib/screenshotAccess";
import { getScreenshotScene, isScreenshotSceneConfigured } from "../screenshotScenes";
import { SCREENSHOTS_MAIN_PATH } from "../routes";
import { phoneShellSx, wordWheelScreenshotAccent } from "../styles/screenshotStyles";
import { normalizeWord } from "../lib/gridReveal";

function parseWords(wordsInUse) {
    if (!wordsInUse) return [];
    return wordsInUse.split("\n").map((w) => w.trim()).filter(Boolean);
}

/** Reveal `count` words; put `highlightWord` first when it is a target word. */
function pickDemoFoundWords(words, count, highlightWord) {
    if (!Array.isArray(words) || words.length === 0) return [];
    const n = Math.min(Math.max(0, Number(count) || 0), words.length);
    if (n === 0) return [];
    const highlight = highlightWord ? normalizeWord(highlightWord) : "";
    if (highlight && words.includes(highlight)) {
        return [highlight, ...words.filter((w) => w !== highlight)].slice(0, n);
    }
    return words.slice(0, n);
}

function backgroundCaptureUrl(background) {
    if (typeof background === "string" && /^https?:\/\//i.test(background)) {
        return background;
    }
    if (isScreenshotIphoneBackgroundKey(background)) {
        return screenshotIphoneBgSrc(background);
    }
    return "";
}

function layoutNeedsPuzzle(layout, isShowcaseAd) {
    if (isShowcaseAd) {
        return true;
    }
    return !["hero", "journey", "daily", "cta", "masterJourney"].includes(layout);
}

/**
 * App Store screenshot — marketing layouts for Word Wheel Quest (01–08).
 * With `?googleAdLandscape=1` or `?googleAdSquare=1`, renders dedicated Google Ads creatives.
 */
export default function WordWheelScreenshotScenePage() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const isLandscapeAd = searchParams.get("googleAdLandscape") === "1";
    const isSquareAd = searchParams.get("googleAdSquare") === "1";
    const isShowcaseAd = isLandscapeAd || isSquareAd;
    const { t, lang } = useScreenshotTranslation();
    const scene = getScreenshotScene(slug);
    const layout = scene?.layout || "gameplay";
    const needsPuzzle = layoutNeedsPuzzle(layout, isShowcaseAd);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [puzzle, setPuzzle] = useState(null);
    const [storyBgUrl, setStoryBgUrl] = useState("");
    const backgroundPuzzleId = scene?.backgroundSlidingId || scene?.backgroundJigsawId || "";
    const [storyBgReady, setStoryBgReady] = useState(!backgroundPuzzleId);

    const viewScene = useMemo(() => {
        if (!scene || !storyBgUrl) return scene;
        return { ...scene, background: storyBgUrl };
    }, [scene, storyBgUrl]);

    const foundWords = useMemo(() => {
        if (!puzzle?.wordsInUse) return [];
        const words = parseWords(puzzle.wordsInUse).map(normalizeWord);
        return pickDemoFoundWords(words, scene?.demoFoundWordCount, scene?.dictionaryWord);
    }, [puzzle, scene?.demoFoundWordCount, scene?.dictionaryWord]);

    const headline = t(screenshotSceneKey("wordWheel", slug, "headline"));
    const subtitle = t(screenshotSceneKey("wordWheel", slug, "subtitle"));

    useEffect(() => {
        const slidingId = scene?.backgroundSlidingId || "";
        const jigsawId = scene?.backgroundJigsawId || "";
        if (!slidingId && !jigsawId) {
            setStoryBgUrl("");
            setStoryBgReady(true);
            return undefined;
        }
        let cancelled = false;
        setStoryBgReady(false);
        setStoryBgUrl("");
        (async () => {
            const url = await resolveStandaloneBackgroundUrl({ slidingId, jigsawId });
            if (cancelled) return;
            setStoryBgUrl(url || "");
            setStoryBgReady(true);
        })();
        return () => {
            cancelled = true;
        };
    }, [scene]);

    useEffect(() => {
        if (isScreenshotSceneExpired(slug)) {
            setLoading(false);
            return undefined;
        }

        if (!scene) {
            setError("Unknown screenshot scene");
            setLoading(false);
            return undefined;
        }

        if (!isScreenshotSceneConfigured(scene)) {
            setError("Screenshot scene is not configured yet");
            setLoading(false);
            return undefined;
        }

        if (!needsPuzzle) {
            setPuzzle(null);
            setError(null);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await loadScreenshotWordWheel(scene);
                if (cancelled) return;
                setPuzzle(data);
                setError(null);
            } catch (e) {
                if (!cancelled) {
                    setError(e?.message ?? "Failed to load word wheel puzzle");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [scene, slug, needsPuzzle]);

    useEffect(() => {
        markScreenshotNotReady();
    }, [slug, lang]);

    useEffect(() => {
        if (loading || error || !storyBgReady) return undefined;
        if (needsPuzzle && !puzzle) return undefined;
        let cancelled = false;
        (async () => {
            await signalScreenshotSceneReady({
                fontFamily: screenshotFontStack(lang),
                lang,
                imageUrl: backgroundCaptureUrl(viewScene?.background),
            });
            if (cancelled) markScreenshotNotReady();
        })();
        return () => {
            cancelled = true;
        };
    }, [loading, error, puzzle, lang, needsPuzzle, storyBgReady, viewScene]);

    if (isScreenshotSceneExpired(slug)) {
        return <ScreenshotExpiredPage />;
    }

    if (!scene) {
        return (
            <Box sx={{ ...phoneShellSx, p: 2 }}>
                <Typography color="error">{t("mobileScreenshot.common.scene-not-found")}</Typography>
                <Link component={RouterLink} to={SCREENSHOTS_MAIN_PATH} sx={{ color: wordWheelScreenshotAccent }}>
                    {t("mobileScreenshot.common.back-to-index")}
                </Link>
            </Box>
        );
    }

    if (!isScreenshotSceneConfigured(scene)) {
        return (
            <Box sx={{ ...phoneShellSx, p: 2 }}>
                <Typography color="error" sx={{ mb: 1 }}>
                    Screenshot {scene.slug} — add journey level in `screenshotScenes.js`.
                </Typography>
                <Link component={RouterLink} to={SCREENSHOTS_MAIN_PATH} sx={{ color: wordWheelScreenshotAccent }}>
                    {t("mobileScreenshot.common.back-to-index")}
                </Link>
            </Box>
        );
    }

    if (loading || !storyBgReady) {
        return (
            <Box sx={{ ...phoneShellSx, alignItems: "center", justifyContent: "center" }}>
                <CircularProgress size={32} sx={{ color: wordWheelScreenshotAccent }} />
            </Box>
        );
    }

    if (error || (needsPuzzle && !puzzle)) {
        return (
            <Box sx={{ ...phoneShellSx, p: 2 }}>
                <Typography color="error" sx={{ mb: 1 }}>
                    {error ?? t("mobileScreenshot.common.could-not-load")}
                </Typography>
                <Link component={RouterLink} to={SCREENSHOTS_MAIN_PATH} sx={{ color: wordWheelScreenshotAccent }}>
                    {t("mobileScreenshot.common.back-to-index")}
                </Link>
            </Box>
        );
    }

    if (isShowcaseAd) {
        return (
            <Box sx={screenshotShellFontSx(lang)}>
                <LandscapeGoogleAdCreative
                    scene={viewScene}
                    puzzle={puzzle}
                    foundWords={foundWords}
                    variant={isSquareAd ? "square" : "landscape"}
                />
            </Box>
        );
    }

    const shared = {
        headline,
        subtitle,
        puzzle,
        foundWords,
        scene: viewScene,
    };

    let content = null;
    switch (layout) {
        case "hero":
            content = (
                <HeroLayout
                    headline={headline}
                    subtitle={subtitle}
                    background={viewScene.background}
                />
            );
            break;
        case "progression":
            content = <GameplayLayout {...shared} edgeBleed />;
            break;
        case "journey":
            content = <JourneyLayout headline={headline} subtitle={subtitle} scene={viewScene} />;
            break;
        case "dictionary":
            content = <DictionaryLayout {...shared} />;
            break;
        case "themes":
            content = <ThemesLayout {...shared} />;
            break;
        case "daily":
            content = <DailyLayout headline={headline} subtitle={subtitle} />;
            break;
        case "cta":
            content = <CtaLayout {...shared} />;
            break;
        case "masterJourney":
            content = <MasterJourneyLayout scene={viewScene} />;
            break;
        case "clue":
            content = <ClueLayout {...shared} />;
            break;
        case "gameplay":
        default:
            content = <GameplayLayout {...shared} />;
            break;
    }

    return (
        <Box sx={screenshotShellFontSx(lang)}>
            {content}
        </Box>
    );
}
